/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-console */
import React, { useMemo, useEffect, useRef, createContext, useContext } from 'react';
import { ChatClient } from '@ably/chat';
import { ChatClientProvider } from '@ably/chat/react';
import useAblyAuth from './AblyAuth';
import AblyCleanupWrapper from './AblyCleanupWrapper';
import Loader from '../../Components/Utility/Loader/Loader';

// Create context to share Ably client instance
const AblyClientContext = createContext(null);

// Custom hook to access the shared Ably client
export const useAblyClient = () => {
  return useContext(AblyClientContext);
};

function AblyWrapper({ children, enableCleanup = true }) {
  const ablyClient = useAblyAuth();
  const chatClientRef = useRef(null);

  // Always call useMemo, but return null if no ablyClient
  const chatClient = useMemo(() => {
    if (!ablyClient) {
      return null;
    }
    console.log('🔧 Creating new ChatClient instance');
    const client = new ChatClient(ablyClient);
    chatClientRef.current = client;
    return client;
  }, [ablyClient]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (chatClientRef.current) {
        console.log('🧹 Cleaning up ChatClient instance');
        try {
          // Only clean up the ChatClient reference - DO NOT close the underlying Ably connection
          // The AblyAuth hook will handle closing the Ably connection properly
          // eslint-disable-next-line no-console
          console.log('🧹 ChatClient cleanup: Clearing reference only');
        } catch (error) {
          console.warn('Error during ChatClient cleanup:', error);
        }
        chatClientRef.current = null;
      }
    };
  }, []);

  // Provide Ably client context even when chatClient is not ready
  // This allows components to use useAblyClient hook for notifications
  if (!chatClient) {
    console.log('🚀 ~ AblyWrapper ~ chatClient not ready:', chatClient);
    // Return a loading state to prevent chat hooks from being called
    // Components using chat-specific hooks (useChatClient, useMessages, etc.)
    // should not render until chatClient is available
    return (
      <AblyClientContext.Provider value={ablyClient}>
        <div className="text-center py-4">
          <Loader />
        </div>
      </AblyClientContext.Provider>
    );
  }
  return (
    <AblyClientContext.Provider value={ablyClient}>
      <ChatClientProvider client={chatClient}>{enableCleanup ? <AblyCleanupWrapper>{children}</AblyCleanupWrapper> : children}</ChatClientProvider>
    </AblyClientContext.Provider>
  );
}

export default AblyWrapper;
